import { DislikeOutlined, LikeOutlined, ReloadOutlined } from "@ant-design/icons";
import type React from "react";
import { useId, useState } from "react";
import { type PrechartRefreshResult, prechartBaseUrl, requestPrechartRefresh, sendPrechartFeedback } from "../prechart";

// The pre-visit brief: what changed since the patient's last visit, composed
// before the clinician opens the chart (backend `prechart/`). Posted into the
// thread by the `pre_visit_brief` custom action and restored from thread
// metadata anchored to its lead-in message. Payload shape:
// docs/precharting/api-contract.md (BriefPayload v2, the composite card).
//
// Top to bottom: header, glance counts, headline, what needs the clinician's
// eyes, the story, what the last visit produced, every fact by date (three
// rows; the full timeline behind "Show all"), quiet notes, what was not read,
// actions. Only the headline and the story are the model's, and they carry
// the AI mark; everything else is built from the facts.
//
// Read-only apart from three calls that never touch the chat stream: source
// chips expand their detail in place, Refresh queues a fresh pre-chart, and
// thumbs up/down store feedback on this brief version.
export interface BriefSource {
  // The fact id ("result:<id>", "doc:<id>", "enc:<id>", "outside:<key>",
  // "outside-proc:<key>"), as the harness's Source.ref.
  ref: string;
  kind?: "result" | "document" | "encounter" | "outside_result" | "outside_procedure" | string;
  label: string;
  unreviewed?: boolean;
  detail?: string | null;
}

export interface BriefItem {
  text: string;
  // "open" is an order with no result (needs_attention only).
  priority?: "high" | "open" | "normal" | string;
  sources?: BriefSource[];
  ai?: boolean;
  // needs_attention only: the item's document names another patient or birth date.
  identity_mismatch?: boolean;
}

// v1 sections: still in the payload for Superdoc's context renderer; the card
// no longer reads them.
export interface BriefSection {
  key: string;
  title: string;
  items?: BriefItem[];
}

export interface BriefCoverage {
  complete?: boolean;
  sources?: Array<{ source: string; status: string; count?: number; detail?: string }>;
  gaps?: string[];
}

export interface BriefAnchor {
  date?: string | null;
  visit_type?: string | null;
  days_ago?: number | null;
  rule?: string | null;
  // "provider": this clinician's own last office visit; "practice": the practice's.
  scope?: "provider" | "practice" | string | null;
  note_signed?: boolean | null;
  // The bundle capped a very old anchor to its maximum window, which spans
  // `window_days` (days from the window start to the appointment).
  window_capped?: boolean | null;
  window_days?: number | null;
}

export interface BriefCounts {
  flagged?: number | null;
  unreviewed?: number | null;
  open_orders?: number | null;
  not_read?: number | null;
  calls?: number | null;
  documents?: number | null;
  outside?: number | null;
  events?: number | null;
}

// A row about what the anchor visit produced ("Ordered", later rows from its note).
export interface BriefPlanRow {
  origin?: "orders" | "note" | string;
  label: string;
  text: string;
  status?: "high" | "open" | "ok" | "normal" | string;
  // origin "orders": every order placed at the visit, and those still without a result.
  order_names?: string[] | null;
  open_names?: string[] | null;
  sources?: BriefSource[];
}

// One fact (or a fold of many) on the timeline.
export interface BriefEvent {
  ref: string;
  kind:
    | "result"
    | "pending"
    | "document"
    | "telephone"
    | "office"
    | "other_visit"
    | "outside_result"
    | "outside_procedure"
    | "fold"
    | string;
  date?: string | null;
  end_date?: string | null;
  arrived?: string | null;
  title: string;
  // result and pending: the order's name.
  name?: string | null;
  sub?: string | null;
  flagged?: boolean;
  unreviewed?: boolean;
  other_provider?: boolean;
  older_care?: boolean;
  open?: boolean;
  read?: boolean;
  identity_mismatch?: boolean;
  count?: number | null;
  sources?: BriefSource[];
}

export interface PreVisitBriefData {
  brief_id: string;
  run_id?: string;
  version?: number;
  status?: string;
  patient_name?: string;
  appointment?: { date?: string | null; time?: string | null; visit_type?: string | null } | null;
  anchor?: BriefAnchor | null;
  is_new_patient?: boolean;
  headline?: string | null;
  composer?: "llm" | "deterministic" | string;
  counts?: BriefCounts | null;
  needs_attention?: BriefItem[];
  // The count before the cap; more than shown ⇒ "+N more in the list below".
  needs_attention_total?: number | null;
  story?: BriefItem[];
  plan?: BriefPlanRow[];
  events?: BriefEvent[];
  quiet?: string[];
  coverage?: BriefCoverage | null;
  sections?: BriefSection[];
  generated_at?: string;
}

interface PreVisitBriefProps {
  data: PreVisitBriefData;
  // The patient the panel is open on — Refresh queues a pre-chart for them.
  // Absent (no patient selected) ⇒ no Refresh.
  clientId?: number;
  // A newer brief is further down the thread: read-only, no actions.
  frozen?: boolean;
}

// "2026-09-30" as a local calendar date (never shifted by the timezone).
const parseDay = (iso?: string | null): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec((iso || "").trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
};

// "Wed, Sep 30"
export const appointmentDayLabel = (iso?: string | null): string => {
  const d = parseDay(iso);
  return d ? d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : iso || "";
};

// "Jul 5", with the year when it isn't the reference year ("Jul 5, 2025").
export const lastVisitDayLabel = (iso?: string | null, referenceYear?: number): string => {
  const d = parseDay(iso);
  if (!d) return iso || "";
  const year = referenceYear ?? new Date().getFullYear();
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(d.getFullYear() !== year ? { year: "numeric" } : {}),
  });
};

export const daysAgoLabel = (days?: number | null): string => {
  if (days == null || Number.isNaN(days)) return "";
  if (days <= 0) return "today";
  return days === 1 ? "1 day ago" : `${days} days ago`;
};

// "2026-09-12" → "Sep 12" (with the year when it isn't the visit's) wherever the
// backend wrote an ISO date into text: it keeps ISO so Superdoc's context stays
// unambiguous; the card reads dates the way its header does.
const ISO_DATE = /\b(\d{4})-(\d{2})-(\d{2})\b/g;
const prettyDates = (text: string, referenceYear?: number): string =>
  text.replace(ISO_DATE, (iso) => lastVisitDayLabel(iso, referenceYear) || iso);

// "2026-09-30" out of a date or datetime, "" without one: compares and sorts as text.
const dayKey = (iso?: string | null): string => /^\d{4}-\d{2}-\d{2}/.exec((iso || "").trim())?.[0] ?? "";

const isToday = (iso?: string | null): boolean => {
  const d = parseDay(iso);
  const now = new Date();
  return (
    !!d && d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate()
  );
};

const monthDay = (d: Date): string => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });

// The timeline's date column: "Sep 14", then "–16" when a stay ends later,
// then the year when it isn't the visit's.
const dateLines = (start?: string | null, end?: string | null, referenceYear?: number): string[] => {
  const s = parseDay(start);
  if (!s) return [];
  const lines = [monthDay(s)];
  const e = parseDay(end);
  if (e && e.getTime() > s.getTime()) {
    const sameMonth = e.getFullYear() === s.getFullYear() && e.getMonth() === s.getMonth();
    lines.push(`–${sameMonth ? e.getDate() : monthDay(e)}`);
  }
  if (referenceYear != null && s.getFullYear() !== referenceYear) lines.push(String(s.getFullYear()));
  return lines;
};

// Oldest first; undated facts (the unreviewed backlog) after the dated ones.
const ascending = <T extends { event: BriefEvent }>(rows: T[]): T[] =>
  rows.slice().sort((a, b) => {
    const ka = dayKey(a.event.date);
    const kb = dayKey(b.event.date);
    if (!ka || !kb) return ka ? -1 : kb ? 1 : 0;
    return ka.localeCompare(kb);
  });

// Fallbacks for a payload without structured names: a pending order's title
// reads "<order> ordered · no result yet"…
const orderName = (title: string): string => title.replace(/\s*ordered\s*·\s*no result yet\s*$/i, "").trim() || title;

// …and the orders row reads "4 orders: HbA1c, BMP, … → 3 back …".
const orderedNames = (text: string): string => {
  const head = text.split("→")[0];
  const colon = head.indexOf(":");
  return (colon >= 0 ? head.slice(colon + 1) : head).trim();
};

const AI_MARK_TITLE = "Model summary, checked against the chart";
const MAX_CHIPS = 4;
const PREVIEW_EVENTS = 3;
const OUTSIDE_KINDS = ["outside_result", "outside_procedure"];

const ClipboardIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }} aria-hidden="true">
    <rect x="3" y="2.5" width="10" height="11.5" rx="1.5" stroke="#6366F1" strokeWidth="1.3" />
    <path d="M6 2.5h4v1.5H6z" fill="#EEF2FF" stroke="#6366F1" strokeWidth="1.3" strokeLinejoin="round" />
    <path d="M5.5 7h5M5.5 9.5h5M5.5 12h3" stroke="#6366F1" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

// Marks the model's lines (headline, story) apart from the facts.
const AiMark: React.FC<{ style?: React.CSSProperties }> = ({ style }) => (
  <span role="img" aria-label={AI_MARK_TITLE} title={AI_MARK_TITLE} style={{ ...styles.aiMark, ...style }}>
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" focusable="false" style={{ display: "block" }}>
      <path
        d="M6 .8c.3 2.6 1.6 3.9 4.2 4.2C7.6 5.3 6.3 6.6 6 9.2 5.7 6.6 4.4 5.3 1.8 5 4.4 4.7 5.7 3.4 6 .8z"
        fill="currentColor"
      />
      <path
        d="M9.7 8.3c.1.8.4 1.1 1.2 1.2-.8.1-1.1.4-1.2 1.2-.1-.8-.4-1.1-1.2-1.2.8-.1 1.1-.4 1.2-1.2z"
        fill="currentColor"
      />
    </svg>
  </span>
);

const FlagGlyph = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" focusable="false" style={styles.quietGlyph}>
    <path d="M2.5 11V1.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    <path d="M3 2h6.6L8.2 4.3 9.6 6.6H3z" fill="currentColor" />
  </svg>
);

// One small glyph per kind of fact (stroke = currentColor).
type GlyphName = "result" | "document" | "telephone" | "office" | "outside" | "pending" | "fold" | "warning";

const GLYPHS: Record<GlyphName, React.ReactNode> = {
  result: (
    <path
      d="M5.5 2h3M6 2v4.2L3.2 11a1 1 0 0 0 .9 1.5h5.8a1 1 0 0 0 .9-1.5L8 6.2V2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  ),
  document: (
    <>
      <path
        d="M4 1.5h4l3 3V12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2.5a1 1 0 0 1 1-1z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
      />
      <path d="M5 8h4M5 10h3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </>
  ),
  telephone: (
    <path
      d="M3.2 2.2 5 2l1.2 2.8-1.3 1a6 6 0 0 0 3.3 3.3l1-1.3L12 9l-.2 1.8c-.1.8-.8 1.3-1.6 1.2A9.5 9.5 0 0 1 2 3.8c-.1-.8.4-1.5 1.2-1.6z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  ),
  office: (
    <>
      <circle cx="7" cy="4.5" r="2.3" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <path
        d="M2.5 12.5c.4-2.6 2.2-4 4.5-4s4.1 1.4 4.5 4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </>
  ),
  outside: (
    <path
      d="M2 12.5h10M3 12.5V4l4-2.5L11 4v8.5M5.5 6.5h3M5.5 9h3"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  ),
  pending: (
    <>
      <circle cx="7" cy="7" r="5" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <path d="M7 4v3.2l2 1.3" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </>
  ),
  fold: (
    <path
      d="M2.5 5.5 7 3l4.5 2.5L7 8z M2.5 8 7 10.5 11.5 8"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  ),
  warning: (
    <>
      <path
        d="M6.5 2.3a.6.6 0 0 1 1 0l5.3 9.4a.6.6 0 0 1-.5.9H1.7a.6.6 0 0 1-.5-.9z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <path d="M7 5.8v2.8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      <circle cx="7" cy="10.6" r=".7" fill="currentColor" />
    </>
  ),
};

const GLYPH_COLOR: Record<GlyphName, string> = {
  result: "#6366F1",
  document: "#6366F1",
  telephone: "#6B7280",
  office: "#6B7280",
  outside: "#0F766E",
  pending: "#B45309",
  fold: "#9CA3AF",
  warning: "#B45309",
};

const KIND_GLYPH: Record<string, GlyphName> = {
  result: "result",
  pending: "pending",
  document: "document",
  telephone: "telephone",
  office: "office",
  other_visit: "office",
  outside_result: "outside",
  outside_procedure: "outside",
  fold: "fold",
};

// Sub-lines that only name the row's kind, which its glyph already shows.
const KIND_ONLY_SUB: Record<string, string> = {
  telephone: "telephone encounter",
  office: "office visit",
  other_visit: "office visit",
};

const subLine = (e: BriefEvent): string => {
  const sub = e.sub?.trim() ?? "";
  return sub.toLowerCase() === KIND_ONLY_SUB[e.kind] ? "" : sub;
};

const glyphFor = (e: BriefEvent): GlyphName => (e.identity_mismatch ? "warning" : KIND_GLYPH[e.kind] ?? "document");

const Glyph: React.FC<{ name: GlyphName }> = ({ name }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    aria-hidden="true"
    focusable="false"
    style={{ ...styles.glyph, color: GLYPH_COLOR[name] }}
  >
    {GLYPHS[name]}
  </svg>
);

const Tag: React.FC<{ tone: React.CSSProperties; children: React.ReactNode }> = ({ tone, children }) => (
  <span style={{ ...styles.tag, ...tone }}>{children}</span>
);

// One source chip. A chip with detail is a toggle button; one without is a
// plain label (nothing to expand, so nothing to click). The Unreviewed badge
// sits beside its chip and wraps under it in a narrow panel, so the label
// stays readable.
const SourceChip: React.FC<{
  source: BriefSource;
  expanded: boolean;
  detailId: string;
  onToggle: () => void;
  badge: boolean;
  year?: number;
}> = ({ source, expanded, detailId, onToggle, badge, year }) => {
  const label = <span style={styles.chipLabel}>{prettyDates(source.label, year)}</span>;
  const chip = source.detail?.trim() ? (
    <button
      type="button"
      style={{ ...styles.chip, ...styles.chipButton, ...(expanded ? styles.chipExpanded : {}) }}
      aria-expanded={expanded}
      aria-controls={detailId}
      onClick={onToggle}
    >
      {label}
    </button>
  ) : (
    <span style={styles.chip}>{label}</span>
  );
  if (!badge || !source.unreviewed) return chip;
  return (
    <span style={styles.chipGroup}>
      {chip}
      <Tag tone={styles.tagUnreviewed}>Unreviewed</Tag>
    </span>
  );
};

interface ChipState {
  idPrefix: string;
  expanded: Set<string>;
  onToggle: (key: string) => void;
  // The visit's year: dates in that year show without it.
  year?: number;
}

// A line's sources, with any tags (`leading`) first in the same row.
// `badges` false: the row carries its own Unreviewed tag (the timeline).
const SourceChips: React.FC<
  ChipState & {
    sources?: BriefSource[];
    keyPrefix: string;
    leading?: React.ReactNode[];
    badges?: boolean;
  }
> = ({ sources, keyPrefix, leading, badges = true, idPrefix, expanded, onToggle, year }) => {
  const [showAll, setShowAll] = useState(false);
  const list = (sources ?? []).filter((s) => !!s?.label?.trim());
  if (!list.length && !leading?.length) return null;
  // A backlog line can cite dozens of documents; keep the row to one glance.
  const shown = showAll ? list : list.slice(0, MAX_CHIPS);
  const open = shown
    .map((s, i) => ({ s, key: `${keyPrefix}:${i}` }))
    .filter(({ s, key }) => expanded.has(key) && !!s.detail?.trim());
  return (
    <>
      <div style={styles.chipRow}>
        {leading}
        {shown.map((s, i) => {
          const key = `${keyPrefix}:${i}`;
          return (
            <SourceChip
              key={key}
              source={s}
              expanded={expanded.has(key)}
              detailId={`${idPrefix}-${key}`}
              onToggle={() => onToggle(key)}
              badge={badges}
              year={year}
            />
          );
        })}
        {list.length > shown.length && (
          <button type="button" style={{ ...styles.chip, ...styles.chipButton }} onClick={() => setShowAll(true)}>
            +{list.length - shown.length} more
          </button>
        )}
      </div>
      {open.map(({ s, key }) => (
        <div key={key} id={`${idPrefix}-${key}`} style={styles.detail}>
          <span style={styles.detailLabel}>{prettyDates(s.label, year)}:</span> {prettyDates(s.detail ?? "", year)}
        </div>
      ))}
    </>
  );
};

// A bulleted line (needs your eyes, the story): red dot for high, hollow amber
// for an open order.
const ItemRow: React.FC<{ item: BriefItem; keyPrefix: string; chips: ChipState }> = ({ item, keyPrefix, chips }) => {
  const priority = item.priority === "high" || item.priority === "open" ? item.priority : "normal";
  const high = priority === "high";
  return (
    <li style={styles.item} data-priority={priority}>
      <span
        style={{ ...styles.dot, ...(high ? styles.dotHigh : priority === "open" ? styles.dotOpen : {}) }}
        aria-hidden="true"
      />
      <div style={styles.itemBody}>
        <span style={{ ...styles.itemText, ...(high ? styles.itemTextHigh : {}) }}>
          {high && <span style={styles.srOnly}>High priority: </span>}
          {item.identity_mismatch && (
            <span title="Identity check failed">
              <Glyph name="warning" />
              <span style={styles.srOnly}>Identity check failed: </span>
            </span>
          )}
          {prettyDates(item.text, chips.year)}
        </span>
        <SourceChips sources={item.sources} keyPrefix={keyPrefix} {...chips} />
      </div>
    </li>
  );
};

type DotTone = "normal" | "high" | "unrev" | "out" | "faint" | "open" | "anchor" | "today";

// The last visit and this visit: indigo rings, a little larger than a dot.
const RING: React.CSSProperties = {
  backgroundColor: "#FFFFFF",
  boxShadow: "none",
  width: "12px",
  height: "12px",
  left: "3px",
  top: "3px",
};

const DOT_TONE: Record<DotTone, React.CSSProperties | undefined> = {
  normal: undefined,
  high: { backgroundColor: "#DC2626", boxShadow: "0 0 0 1px #DC2626" },
  unrev: { backgroundColor: "#B45309", boxShadow: "0 0 0 1px #B45309" },
  out: { backgroundColor: "#0F766E", boxShadow: "0 0 0 1px #0F766E" },
  faint: { backgroundColor: "#E5E7EB", boxShadow: "0 0 0 1px #E5E7EB" },
  open: { backgroundColor: "#FFFFFF", boxShadow: "0 0 0 1px #B45309" },
  anchor: { ...RING, border: "2px solid #6366F1" },
  today: { ...RING, border: "2px dashed #6366F1" },
};

// A plan row's status dot; "normal" is the plain gray dot.
const PLAN_DOT: Record<"high" | "open" | "ok" | "normal", React.CSSProperties | undefined> = {
  high: { backgroundColor: "#DC2626" },
  open: { width: "9px", height: "9px", backgroundColor: "#FFFFFF", border: "2px solid #B45309", marginTop: "5px" },
  ok: { backgroundColor: "#047857" },
  normal: undefined,
};

// Folds are the quiet rows: faint whatever they hold.
const eventDot = (e: BriefEvent): DotTone =>
  e.kind === "pending"
    ? "open"
    : e.kind === "fold"
      ? "faint"
      : e.flagged
        ? "high"
        : e.unreviewed
          ? "unrev"
          : OUTSIDE_KINDS.includes(e.kind)
            ? "out"
            : "normal";

// The timeline's rail cell: a line (cut at the first and last rows, dashed
// under an open order) and the row's dot.
const Rail: React.FC<{ tone: DotTone; first: boolean; last: boolean }> = ({ tone, first, last }) => (
  <div style={styles.tlRail} aria-hidden="true">
    <span
      style={{
        ...styles.tlLine,
        ...(first ? styles.tlLineFirst : {}),
        ...(last ? styles.tlLineLast : {}),
        ...(tone === "open" ? styles.tlLineDashed : {}),
      }}
    />
    <span style={{ ...styles.tlDot, ...DOT_TONE[tone] }} />
  </div>
);

const DateCell: React.FC<{ lines: string[]; style: React.CSSProperties }> = ({ lines, style }) => (
  <div style={style}>
    {lines.map((line, i) => (
      <span key={i} style={{ display: "block" }}>
        {line}
      </span>
    ))}
  </div>
);

type TimelineRow = { type: "event"; event: BriefEvent; index: number } | { type: "anchor" } | { type: "today" };

type FeedbackState = { phase: "idle" | "sending" | "sent" | "error"; helpful?: boolean };

const PreVisitBrief: React.FC<PreVisitBriefProps> = ({ data, clientId, frozen }) => {
  const idPrefix = useId();
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [refresh, setRefresh] = useState<"idle" | "sending" | PrechartRefreshResult>("idle");
  const [feedback, setFeedback] = useState<FeedbackState>({ phase: "idle" });

  if (!data?.brief_id) return null;

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const appt = data.appointment;
  const anchor = data.anchor;
  const referenceYear = parseDay(appt?.date)?.getFullYear();
  const chips: ChipState = { idPrefix, expanded, onToggle: toggle, year: referenceYear };
  const pretty = (text?: string | null) => prettyDates(text ?? "", referenceYear);
  const dayLabel = (iso?: string | null) => lastVisitDayLabel(iso, referenceYear);
  const yourVisit = anchor?.scope === "provider";
  const aiComposed = data.composer !== "deterministic";

  // ── Header ──
  const apptLine = appt ? [appointmentDayLabel(appt.date), appt.time, appt.visit_type].filter(Boolean).join(" · ") : "";
  const windowDays = anchor?.window_days ?? 0;
  const windowNote = !anchor?.window_capped
    ? ""
    : windowDays > 0
      ? `showing the last ${windowDays} days`
      : "showing a shorter window";
  // The anchor is this clinician's own visits, so "new" means new to them,
  // not to the practice.
  const lastVisitLine = data.is_new_patient
    ? "No earlier visit with you on record"
    : anchor?.date
      ? [
          `${yourVisit ? "Your last visit" : "Last visit"} ${dayLabel(anchor.date)}`,
          anchor.visit_type,
          daysAgoLabel(anchor.days_ago),
          anchor.note_signed === false ? "note not signed" : "",
          windowNote,
        ]
          .filter(Boolean)
          .join(" · ")
      : "";

  // ── Glance: only the counts that are not zero; an absent strip (older payload) claims nothing ──
  const counts = data.counts;
  const pills = counts
    ? [
        { key: "flagged", n: counts.flagged, label: "flagged", tone: styles.pillRed },
        { key: "unreviewed", n: counts.unreviewed, label: "unreviewed", tone: styles.pillAmber },
        { key: "open_orders", n: counts.open_orders, label: "open orders", tone: styles.pillAmber },
        { key: "not_read", n: counts.not_read, label: "not read", tone: undefined },
      ].filter((p) => (p.n ?? 0) > 0)
    : [];

  // ── Lists (every missing array reads as empty) ──
  const withText = (items?: BriefItem[] | null) => (items ?? []).filter((it) => !!it?.text?.trim());
  const needs = withText(data.needs_attention);
  const moreNeeds = (data.needs_attention_total ?? 0) - (data.needs_attention ?? []).length;
  const story = withText(data.story);
  const plan = (data.plan ?? []).filter((row) => !!row?.text?.trim());
  const quiet = (data.quiet ?? []).filter((q) => typeof q === "string" && !!q.trim());
  const events = (data.events ?? [])
    .filter((e) => !!e?.title?.trim())
    .map((event, index) => ({ type: "event" as const, event, index }));
  const coverageGaps = (data.coverage?.gaps ?? []).filter((g) => !!g?.trim());
  const incomplete = data.coverage?.complete === false;
  const coverageText = pretty(coverageGaps.length ? coverageGaps.join("; ") : "some sources");

  const planTitle = yourVisit
    ? anchor?.date
      ? `From your ${dayLabel(anchor.date)} visit`
      : "From your last visit"
    : "From the last visit";

  // ── Everything, by date ──
  const preview = events
    .filter(({ event }) => event.kind !== "fold")
    .sort((a, b) => dayKey(b.event.date).localeCompare(dayKey(a.event.date)))
    .slice(0, PREVIEW_EVENTS);
  const eventCount = `${events.length} ${events.length === 1 ? "event" : "events"}`;
  const ledgerId = `${idPrefix}-ledger`;

  const timelineGroups = (): Array<{ label?: string; rows: TimelineRow[] }> => {
    const anchorKey = dayKey(anchor?.date);
    const groups: Array<{ label?: string; rows: TimelineRow[] }> = [];
    if (anchorKey) {
      const before = events.filter(({ event }) => !dayKey(event.date) || dayKey(event.date) < anchorKey);
      const since = events.filter(({ event }) => !!dayKey(event.date) && dayKey(event.date) >= anchorKey);
      if (before.length) groups.push({ label: "Before the last visit, arrived since", rows: ascending(before) });
      groups.push({ label: "Since the last visit", rows: [{ type: "anchor" }, ...ascending(since)] });
    } else {
      groups.push({ rows: ascending(events) });
    }
    if (appt?.date || appt?.time) groups[groups.length - 1].rows.push({ type: "today" });
    return groups;
  };

  const ordersRow = plan.find((row) => row.origin === "orders");
  const anchorNode = [
    yourVisit ? "Your last visit" : "Last visit",
    anchor?.visit_type,
    anchor?.note_signed === true ? "note signed" : anchor?.note_signed === false ? "note not signed" : "",
  ]
    .filter(Boolean)
    .join(" · ");
  const orderNames = (ordersRow?.order_names ?? []).filter((n) => !!n?.trim());
  const anchorSub = ordersRow
    ? `Ordered: ${orderNames.length ? orderNames.join(", ") : orderedNames(ordersRow.text)}`
    : "";
  const todayNode = [isToday(appt?.date) ? "Today" : "This visit", appt?.time, appt?.visit_type]
    .filter(Boolean)
    .join(" · ");
  const stillOpen = ascending(events.filter(({ event }) => event.kind === "pending"))
    .map(({ event }) => {
      const name = event.name?.trim() || orderName(event.title);
      const ordered = dayKey(event.date);
      return ordered ? `${name} (ordered ${dayLabel(ordered)})` : name;
    })
    .join(" · ");

  const renderRow = (row: TimelineRow, first: boolean, last: boolean) => {
    const bodyStyle = { ...styles.tlBody, ...(last ? { paddingBottom: 0 } : {}) };
    if (row.type !== "event") {
      const isAnchor = row.type === "anchor";
      const sub = isAnchor ? anchorSub : stillOpen ? `Still open: ${stillOpen}` : "";
      return (
        <li key={row.type} style={styles.tlRow} data-kind={row.type}>
          <DateCell
            lines={dateLines(isAnchor ? anchor?.date : appt?.date, null, referenceYear)}
            style={styles.tlDate}
          />
          <Rail tone={row.type} first={first} last={last} />
          <div style={bodyStyle}>
            <div style={styles.nodeLabel}>{isAnchor ? anchorNode : todayNode}</div>
            {sub && <div style={styles.tlSub}>{sub}</div>}
          </div>
        </li>
      );
    }
    const { event, index } = row;
    const tags = [
      event.unreviewed && (
        <Tag key="unreviewed" tone={styles.tagUnreviewed}>
          Unreviewed
        </Tag>
      ),
      (event.kind === "other_visit" || event.other_provider) && (
        <Tag key="other" tone={styles.tagOther}>
          Another provider
        </Tag>
      ),
      OUTSIDE_KINDS.includes(event.kind) && (
        <Tag key="prisma" tone={styles.tagOther}>
          Prisma
        </Tag>
      ),
    ].filter(Boolean);
    const muted = event.kind === "fold" || event.kind === "pending";
    const sub = subLine(event);
    // A label-only chip repeats the row's title; keep the ones that open a detail.
    const expandable = (event.sources ?? []).filter((s) => !!s?.detail?.trim());
    return (
      <li key={`e${index}`} style={styles.tlRow} data-kind={event.kind}>
        <DateCell lines={dateLines(event.date, event.end_date, referenceYear)} style={styles.tlDate} />
        <Rail tone={eventDot(event)} first={first} last={last} />
        <div style={bodyStyle}>
          <div
            style={{
              ...styles.tlTitle,
              ...(event.flagged ? styles.tlTitleHigh : muted ? styles.tlTitleMuted : {}),
            }}
          >
            {event.identity_mismatch ? (
              <span title="Identity check failed">
                <Glyph name="warning" />
                <span style={styles.srOnly}>Identity check failed: </span>
              </span>
            ) : (
              <Glyph name={glyphFor(event)} />
            )}
            {event.flagged && <span style={styles.srOnly}>Flagged: </span>}
            {pretty(event.title)}
          </div>
          {sub && <div style={styles.tlSub}>{pretty(sub)}</div>}
          <SourceChips sources={expandable} keyPrefix={`e${index}`} leading={tags} badges={false} {...chips} />
        </div>
      </li>
    );
  };

  const renderTimeline = () => {
    const groups = timelineGroups();
    return groups.map((group, g) => (
      <div key={group.label ?? "all"}>
        {group.label && <div style={styles.tlGroup}>{group.label}</div>}
        <ol style={styles.tlList}>
          {group.rows.map((row, i) =>
            renderRow(row, g === 0 && i === 0, g === groups.length - 1 && i === group.rows.length - 1),
          )}
        </ol>
      </div>
    ));
  };

  // ── Actions ──
  const onRefresh = async () => {
    if (!clientId || frozen || refresh === "sending") return;
    setRefresh("sending");
    setRefresh(await requestPrechartRefresh(prechartBaseUrl(), clientId));
  };

  const onFeedback = async (helpful: boolean) => {
    if (frozen || feedback.phase === "sending" || feedback.phase === "sent") return;
    setFeedback({ phase: "sending", helpful });
    const ok = await sendPrechartFeedback(prechartBaseUrl(), data.brief_id, helpful, clientId);
    setFeedback(ok ? { phase: "sent", helpful } : { phase: "error" });
  };

  // Only a failed request can be retried; queued / running / turned off stay put.
  const refreshLocked = refresh !== "idle" && refresh !== "error";
  const refreshLabel =
    refresh === "sending" ? "Refreshing…" : refresh === "queued" || refresh === "in_progress" ? "Queued" : "Refresh";
  const refreshMessage =
    refresh === "queued"
      ? "Refresh queued. The updated brief shows the next time you open this patient."
      : refresh === "in_progress"
        ? "A refresh is already running for this visit."
        : refresh === "disabled"
          ? "Pre-charting is turned off for you."
          : refresh === "error"
            ? "Couldn't queue a refresh. Try again."
            : "";
  const feedbackLocked = feedback.phase === "sending" || feedback.phase === "sent";
  const feedbackMessage =
    feedback.phase === "sent"
      ? "Thanks for the feedback."
      : feedback.phase === "error"
        ? "Couldn't send feedback. Try again."
        : "";

  return (
    <section style={{ ...styles.container, ...(frozen ? styles.frozenBox : {}) }} aria-label="Pre-visit brief">
      <div style={styles.header}>
        <div style={{ ...styles.iconBadge, ...(frozen ? styles.iconBadgeFrozen : {}) }}>
          <ClipboardIcon />
        </div>
        <div style={styles.headerText}>
          <span style={styles.title}>Pre-visit brief</span>
          {apptLine && <span style={styles.meta}>{apptLine}</span>}
          {lastVisitLine && <span style={styles.meta}>{lastVisitLine}</span>}
          {!aiComposed && <span style={styles.plainNote}>Plain listing (AI unavailable)</span>}
        </div>
      </div>

      {frozen && <div style={styles.frozenCaption}>An updated brief is further down the thread.</div>}

      {counts && (
        <ul style={styles.glance} aria-label="At a glance">
          {pills.length ? (
            pills.map((p) => (
              <li key={p.key} style={{ ...styles.pill, ...p.tone }}>
                <b style={{ ...styles.pillCount, ...(p.tone ? { color: "inherit" } : {}) }}>{p.n}</b> {p.label}
              </li>
            ))
          ) : (
            <li style={styles.pill}>Nothing flagged</li>
          )}
        </ul>
      )}

      {data.headline?.trim() && (
        <div style={styles.headline}>
          {aiComposed && <AiMark />}
          {pretty(data.headline)}
        </div>
      )}

      {needs.length > 0 && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Needs your eyes</div>
          <ul style={styles.itemList}>
            {needs.map((item, i) => (
              <ItemRow key={`n${i}`} item={item} keyPrefix={`n${i}`} chips={chips} />
            ))}
          </ul>
          {moreNeeds > 0 && <div style={styles.moreNeeds}>+{moreNeeds} more in the list below</div>}
        </div>
      )}

      {story.length > 0 && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>
            What happened
            <AiMark style={styles.aiMarkInTitle} />
          </div>
          <ul style={styles.itemList}>
            {story.map((item, i) => (
              <ItemRow key={`s${i}`} item={{ ...item, priority: "normal" }} keyPrefix={`s${i}`} chips={chips} />
            ))}
          </ul>
        </div>
      )}

      {plan.length > 0 && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>{planTitle}</div>
          <ul style={styles.planList}>
            {plan.map((row, i) => {
              const status = (["high", "open", "ok"] as const).find((st) => st === row.status) ?? "normal";
              return (
                <li key={`p${i}`} style={styles.planRow} data-status={status}>
                  <span style={styles.planLabel}>{row.label}</span>
                  <div style={styles.planBody}>
                    <span style={{ ...styles.planDot, ...PLAN_DOT[status] }} aria-hidden="true" />
                    <div style={styles.planText}>
                      <span>{pretty(row.text)}</span>
                      <SourceChips sources={row.sources} keyPrefix={`p${i}`} {...chips} />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {events.length > 0 && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Everything, by date</div>
          <div id={ledgerId}>
            {ledgerOpen ? (
              renderTimeline()
            ) : (
              <ul style={styles.previewList}>
                {preview.map(({ event, index }) => (
                  <li key={`v${index}`} style={styles.previewRow} data-kind={event.kind}>
                    <DateCell lines={dateLines(event.date, null, referenceYear)} style={styles.previewDate} />
                    <span
                      style={{ ...styles.previewText, ...(event.flagged ? styles.previewTextHigh : {}) }}
                      title={pretty(event.title)}
                    >
                      <Glyph name={glyphFor(event)} />
                      {event.flagged && <span style={styles.srOnly}>Flagged: </span>}
                      {pretty(event.title)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button
            type="button"
            style={styles.ledgerToggle}
            aria-expanded={ledgerOpen}
            aria-controls={ledgerId}
            onClick={() => setLedgerOpen((open) => !open)}
          >
            <span>
              <b style={styles.ledgerCount}>{eventCount}</b>
              {anchor?.date ? ` since ${dayLabel(anchor.date)}` : ""}
            </span>
            <span style={styles.link}>{ledgerOpen ? "Show less" : "Show all"}</span>
          </button>
        </div>
      )}

      {quiet.length > 0 && (
        <ul style={styles.quietList}>
          {quiet.map((note, i) => (
            <li key={`q${i}`} style={styles.quiet}>
              <FlagGlyph />
              <span>{pretty(note)}</span>
            </li>
          ))}
        </ul>
      )}

      {Array.isArray(data.events) && data.events.length === 0 && (
        <div style={styles.empty}>Nothing new found in the records read.</div>
      )}

      {incomplete && (
        <div style={styles.coverage}>
          <b style={styles.coverageLabel}>Not read:</b> {coverageText}
          {coverageText.endsWith(".") ? "" : "."}
        </div>
      )}

      {!frozen && (
        <div style={styles.actions}>
          {clientId ? (
            <button
              type="button"
              onClick={onRefresh}
              disabled={refreshLocked}
              style={{ ...styles.ghostButton, ...(refreshLocked ? styles.buttonDisabled : {}) }}
            >
              <ReloadOutlined spin={refresh === "sending"} style={{ fontSize: 12 }} />
              {refreshLabel}
            </button>
          ) : (
            <span />
          )}
          <div style={styles.feedback}>
            <button
              type="button"
              aria-label="Helpful"
              title="Helpful"
              aria-pressed={feedback.helpful === true && feedback.phase !== "error"}
              disabled={feedbackLocked}
              onClick={() => onFeedback(true)}
              style={{
                ...styles.thumb,
                ...(feedback.helpful === true && feedbackLocked ? styles.thumbPicked : {}),
                ...(feedbackLocked && feedback.helpful !== true ? styles.buttonDisabled : {}),
              }}
            >
              <LikeOutlined aria-hidden="true" />
            </button>
            <button
              type="button"
              aria-label="Not helpful"
              title="Not helpful"
              aria-pressed={feedback.helpful === false && feedback.phase !== "error"}
              disabled={feedbackLocked}
              onClick={() => onFeedback(false)}
              style={{
                ...styles.thumb,
                ...(feedback.helpful === false && feedbackLocked ? styles.thumbPicked : {}),
                ...(feedbackLocked && feedback.helpful !== false ? styles.buttonDisabled : {}),
              }}
            >
              <DislikeOutlined aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      {!frozen && (refreshMessage || feedbackMessage) && (
        <div style={styles.status} role="status">
          {[refreshMessage, feedbackMessage].filter(Boolean).join(" ")}
        </div>
      )}
    </section>
  );
};

const listReset: React.CSSProperties = { listStyle: "none", margin: 0, padding: 0 };

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "14px",
    marginTop: "8px",
    borderRadius: "14px",
    border: "1px solid #E2E4F0",
    backgroundColor: "#FFFFFF",
    boxShadow: "0 1px 2px rgba(16, 24, 40, 0.05)",
    // Ancestors center-align text; every widget defends itself.
    textAlign: "left",
  },
  frozenBox: { backgroundColor: "#F9FAFB", border: "1px solid #E5E7EB", boxShadow: "none" },
  frozenCaption: { fontSize: "12px", fontWeight: 500, color: "#9CA3AF", lineHeight: 1.4 },
  header: { display: "flex", alignItems: "flex-start", gap: "9px" },
  iconBadge: {
    width: "26px",
    height: "26px",
    borderRadius: "8px",
    backgroundColor: "#EEF2FF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  iconBadgeFrozen: { backgroundColor: "#F3F4F6", filter: "grayscale(1)" },
  headerText: { display: "flex", flexDirection: "column", gap: "1px", minWidth: 0, paddingTop: "3px" },
  title: { fontSize: "13.5px", fontWeight: 600, color: "#111827", lineHeight: 1.45 },
  meta: { fontSize: "12px", color: "#6B7280", lineHeight: 1.4 },
  plainNote: { fontSize: "11.5px", fontStyle: "italic", color: "#9CA3AF", lineHeight: 1.4 },

  // Glance pills
  glance: { ...listReset, display: "flex", flexWrap: "wrap", gap: "6px" },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "4px 9px",
    borderRadius: "8px",
    border: "1px solid #E5E7EB",
    backgroundColor: "#F9FAFB",
    fontSize: "12px",
    color: "#4B5563",
    lineHeight: 1.3,
  },
  pillRed: { border: "1px solid #FEE2E2", backgroundColor: "#FEF2F2", color: "#B91C1C" },
  pillAmber: { border: "1px solid #FDE68A", backgroundColor: "#FFFBEB", color: "#B45309" },
  pillCount: { fontWeight: 700, color: "#111827" },

  headline: { fontSize: "13px", fontWeight: 500, color: "#111827", lineHeight: 1.5 },
  aiMark: { display: "inline-flex", verticalAlign: "-1px", marginRight: "5px", color: "#6366F1" },
  aiMarkInTitle: { marginRight: 0 },

  // Sections and their bulleted lines
  section: { display: "flex", flexDirection: "column", gap: "6px" },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "11.5px",
    fontWeight: 600,
    color: "#6B7280",
    lineHeight: 1.4,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
  },
  itemList: { ...listReset, display: "flex", flexDirection: "column", gap: "8px" },
  item: { display: "flex", alignItems: "flex-start", gap: "8px" },
  dot: {
    width: "6px",
    height: "6px",
    boxSizing: "border-box",
    borderRadius: "999px",
    backgroundColor: "#D1D5DB",
    flexShrink: 0,
    marginTop: "7px",
  },
  dotHigh: { width: "7px", height: "7px", backgroundColor: "#DC2626", marginTop: "6px" },
  dotOpen: { width: "8px", height: "8px", backgroundColor: "#FFFFFF", border: "2px solid #B45309", marginTop: "5px" },
  itemBody: { display: "flex", flexDirection: "column", gap: "4px", minWidth: 0, flex: 1 },
  itemText: { fontSize: "13px", color: "#374151", lineHeight: 1.45, overflowWrap: "anywhere" },
  itemTextHigh: { fontWeight: 600, color: "#111827" },
  moreNeeds: { fontSize: "12px", color: "#6B7280", lineHeight: 1.4, paddingLeft: "15px" },
  srOnly: {
    position: "absolute",
    width: "1px",
    height: "1px",
    padding: 0,
    margin: "-1px",
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    border: 0,
  },

  // Plan rows: label | status dot | text
  planList: { ...listReset, display: "flex", flexDirection: "column", gap: "6px" },
  planRow: { display: "grid", gridTemplateColumns: "68px 1fr", columnGap: "8px", alignItems: "start" },
  planLabel: { fontSize: "11.5px", color: "#6B7280", lineHeight: 1.4, paddingTop: "1px", overflowWrap: "anywhere" },
  planBody: { display: "flex", alignItems: "flex-start", gap: "6px", minWidth: 0 },
  planDot: {
    width: "8px",
    height: "8px",
    boxSizing: "border-box",
    borderRadius: "999px",
    backgroundColor: "#D1D5DB",
    flexShrink: 0,
    marginTop: "6px",
  },
  planText: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    minWidth: 0,
    flex: 1,
    fontSize: "12.5px",
    color: "#374151",
    lineHeight: 1.45,
    overflowWrap: "anywhere",
  },

  // Everything, by date: the three newest rows
  previewList: { ...listReset, display: "flex", flexDirection: "column", gap: "4px" },
  previewRow: {
    display: "grid",
    gridTemplateColumns: "44px 1fr",
    columnGap: "8px",
    fontSize: "12.5px",
    color: "#374151",
    lineHeight: 1.45,
  },
  previewDate: {
    color: "#6B7280",
    fontSize: "11.5px",
    fontVariantNumeric: "tabular-nums",
    paddingTop: "1px",
    textAlign: "right",
    whiteSpace: "nowrap",
  },
  previewText: { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  previewTextHigh: { fontWeight: 600, color: "#111827" },
  ledgerToggle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    width: "100%",
    boxSizing: "border-box",
    padding: "7px 10px",
    border: "1px dashed #E5E7EB",
    borderRadius: "9px",
    backgroundColor: "transparent",
    fontSize: "12.5px",
    color: "#4B5563",
    lineHeight: 1.4,
    fontFamily: "inherit",
    textAlign: "left",
    cursor: "pointer",
  },
  ledgerCount: { fontWeight: 600, color: "#111827" },
  link: { color: "#4338CA", fontWeight: 500, flexShrink: 0 },

  // Everything, by date: the timeline (date | rail | event)
  tlGroup: {
    fontSize: "11px",
    color: "#9CA3AF",
    lineHeight: 1.4,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    // Past the date column (44px), the rail (18px) and their gaps.
    padding: "0 0 8px 74px",
  },
  tlList: { ...listReset, display: "flex", flexDirection: "column" },
  tlRow: { display: "grid", gridTemplateColumns: "44px 18px 1fr", columnGap: "6px" },
  tlDate: {
    fontSize: "11px",
    color: "#6B7280",
    lineHeight: 1.3,
    paddingTop: "2px",
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  tlRail: { position: "relative" },
  tlLine: { position: "absolute", left: "8px", top: 0, bottom: 0, width: "2px", background: "#E5E7EB" },
  tlLineFirst: { top: "8px" },
  tlLineLast: { bottom: "auto", height: "8px" },
  tlLineDashed: { background: "repeating-linear-gradient(to bottom, #D1D5DB 0 3px, transparent 3px 6px)" },
  tlDot: {
    position: "absolute",
    left: "4px",
    top: "4px",
    width: "10px",
    height: "10px",
    boxSizing: "border-box",
    borderRadius: "999px",
    backgroundColor: "#D1D5DB",
    border: "2px solid #FFFFFF",
    boxShadow: "0 0 0 1px #D1D5DB",
  },
  tlBody: { display: "flex", flexDirection: "column", gap: "3px", minWidth: 0, paddingBottom: "12px" },
  tlTitle: { fontSize: "13px", color: "#374151", lineHeight: 1.4, overflowWrap: "anywhere" },
  tlTitleHigh: { fontWeight: 600, color: "#111827" },
  tlTitleMuted: { color: "#6B7280" },
  tlSub: { fontSize: "11.5px", color: "#6B7280", lineHeight: 1.4, overflowWrap: "anywhere" },
  nodeLabel: { fontSize: "12px", fontWeight: 600, color: "#4338CA", lineHeight: 1.4 },
  glyph: {
    display: "inline-block",
    width: "14px",
    height: "14px",
    verticalAlign: "-3px",
    marginRight: "3px",
  },
  tag: {
    fontSize: "10.5px",
    fontWeight: 600,
    lineHeight: 1,
    padding: "2px 6px",
    borderRadius: "999px",
    whiteSpace: "nowrap",
  },
  tagUnreviewed: { color: "#B45309", backgroundColor: "#FFFBEB", border: "1px solid #FDE68A" },
  tagOther: { color: "#0F766E", backgroundColor: "#F0FDFA", border: "1px solid #99F6E4" },

  // Quiet notes and coverage
  quietList: { ...listReset, display: "flex", flexDirection: "column", gap: "4px" },
  quiet: {
    display: "flex",
    alignItems: "flex-start",
    gap: "6px",
    fontSize: "12px",
    color: "#6B7280",
    lineHeight: 1.45,
  },
  quietGlyph: { display: "block", flexShrink: 0, marginTop: "3px", color: "#B45309" },
  empty: { fontSize: "12.5px", color: "#6B7280", lineHeight: 1.45 },
  coverage: { fontSize: "11.5px", color: "#9CA3AF", lineHeight: 1.45 },
  coverageLabel: { fontWeight: 600, color: "#6B7280" },

  // Source chips
  chipRow: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px" },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    maxWidth: "100%",
    boxSizing: "border-box",
    padding: "2px 8px",
    borderRadius: "999px",
    // Longhands: the expanded state swaps only the colour.
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "#E0E7FF",
    backgroundColor: "#F5F7FF",
    fontSize: "11.5px",
    fontWeight: 500,
    color: "#4338CA",
    lineHeight: 1.5,
    fontFamily: "inherit",
  },
  chipGroup: { display: "inline-flex", flexWrap: "wrap", alignItems: "center", gap: "4px", maxWidth: "100%" },
  chipButton: { cursor: "pointer" },
  chipExpanded: { borderColor: "#A5B4FC", backgroundColor: "#EEF2FF" },
  chipLabel: { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  detail: {
    fontSize: "12px",
    color: "#4B5563",
    lineHeight: 1.45,
    padding: "6px 9px",
    borderRadius: "8px",
    backgroundColor: "#F9FAFB",
    borderLeft: "2px solid #C7D2FE",
    whiteSpace: "pre-wrap",
  },
  detailLabel: { fontWeight: 600, color: "#374151" },

  // Actions
  actions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    borderTop: "1px solid #F0F1F5",
    paddingTop: "8px",
  },
  ghostButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "5px 10px",
    fontSize: "12.5px",
    fontWeight: 500,
    borderRadius: "8px",
    border: "1px solid #D6D9E0",
    backgroundColor: "#FFFFFF",
    color: "#374151",
    cursor: "pointer",
    fontFamily: "inherit",
    lineHeight: 1.4,
  },
  feedback: { display: "flex", gap: "4px" },
  thumb: {
    width: "30px",
    height: "28px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
    borderRadius: "8px",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "transparent",
    backgroundColor: "transparent",
    cursor: "pointer",
    fontFamily: "inherit",
    lineHeight: 1,
  },
  thumbPicked: { borderColor: "#C7D2FE", backgroundColor: "#EEF2FF", cursor: "default" },
  buttonDisabled: { opacity: 0.55, cursor: "not-allowed" },
  status: { fontSize: "12px", color: "#6B7280", lineHeight: 1.4 },
};

export default PreVisitBrief;
